import React from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Progress } from '../ui/progress';
import { interpretScore } from '@/utils/scoring';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGoogleUserData } from '../../utils/googleUserData';

interface UserProfileProps {
  session: any;
  lastScore: number | null;
  lastCategory: string | null;
}

const UserProfile: React.FC<UserProfileProps> = ({ session, lastScore, lastCategory }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Get user metadata
  const userMetadata = session?.user?.user_metadata || {};
  
  // Obtener datos de Google desde localStorage como respaldo
  const googleUserData = React.useMemo(() => {
    return getGoogleUserData();
  }, []);
  
  const avatarUrl = userMetadata.avatar_url || userMetadata.picture || googleUserData?.avatar_url;
  const userName = userMetadata.full_name || userMetadata.name || googleUserData?.full_name;
  
  // Debug logs
  console.log('UserProfile - Debug info:', {
    userMetadata,
    googleUserData,
    finalAvatarUrl: avatarUrl,
    finalUserName: userName,
    hasSession: !!session
  });
  
  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get color based on score
  const getScoreColor = (score: number | null) => {
    if (score === null) return 'gray';
    if (score <= 50) return 'sangre';
    if (score <= 80) return 'warning';
    return 'fuerte';
  };
  
  const startNewEvaluation = () => {
    navigate('/questionnaire');
  };
  
  return (
    <div className="card mb-6">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={userName || 'Usuario'} />
          ) : null}
          <AvatarFallback className="bg-sangre-100 text-sangre-600 text-xl font-medium">
            {getInitials(userName)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h3 className="font-medium text-lg text-gray-800">{userName || 'Usuario'}</h3>
          <p className="text-sm text-gray-500">{session.user.email}</p>
        </div>
      </div>
      
      {/* Last Score Display */}
      {lastScore !== null && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{t('profile.anemiaStatus')}</span>
            </div>
            <span className={`text-sm font-medium text-${getScoreColor(lastScore)}-600`}>
              {lastCategory === 'high' && t('profile.riskLevels.high')}
              {lastCategory === 'moderate' && t('profile.riskLevels.moderate')}
              {lastCategory === 'low' && t('profile.riskLevels.low')}
            </span>
          </div>
          <Progress value={lastScore} className="h-2" />
          <p className="text-center text-xs mt-1 text-gray-500">{lastScore}/100</p>
          
          <button 
            onClick={startNewEvaluation}
            className="w-full mt-4 flex items-center justify-center text-sm font-medium text-gray-700 py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('profile.newEvaluation')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
